import { Typography, Table } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import axios from "api/axios";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import {
  calculatePercentage,
  formatDataGeneral,
  getAttributeValue,
  getFileLinkIfExist,
  getFirstFileLinkIfExist,
} from "utils/utils";
import { transformDpatTableRows, scoreOnlyIfAllEvidenceComplete, scoreOnlyIfDocumentLinksComplete } from "utils/renderDpatEvidenceLink";
import { filterRecordsByDpatYearAttribute, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import { buildSearchableColumns, getDpatTablePagination } from "utils/dpatSearchableTable";

const COMMITTEE_PROGRAM = "NdogHaFyDuI";
const COMMITTEE_REPORT_STAGE = "ogNvkxrU9bM";
const APPOINTMENT_LETTER_FILE = "cIM3xSMA44J";
const AAP_PROGRAM = "ArLnAxhykoz";
const DOCUMENTS_HUB_PROGRAM = "SERtHvYVHRT";
const ACTIVITY_STATUS_ELEMENT = "SZcHb5mvjJx";
const MONITORING_REPORT_FILE = "MjPmcbueEdU";
const GENDER_FOCUS_ACTIVITY = "Gender Focus Activity";

function buildFileUrl(eventId, dataElementId) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementId}`;
}

function getActivityFocus(item) {
  const focus = getAttributeValue("Activity Focus", item);
  if (focus !== "N/A") {
    return focus;
  }
  return getAttributeValue("DPAT | AAP Focus Activity", item);
}

function isGenderFocusActivity(item) {
  return getActivityFocus(item) === GENDER_FOCUS_ACTIVITY;
}

const DeepeningGenderMainstreaming = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [subCommittees, setSubCommittees] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [femalePercentage, setFemalePercentage] = useState(0);
  const [genderActivityPercentage, setGenderActivityPercentage] = useState(0);
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [maxScore, setMaxScore] = useState(3);

  const { Title } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI4",
      area: "Social Protection, Gender and Nutrition",
      maxScore,
      data: documents,
      members: subCommittees,
      subCommittees,
      scorei,
      scoreii,
      percentage: femalePercentage,
      genderActivityPercentage,
    }),
  }));

  const DPAT_SLUG = "sdi4.0-4.4";

  const loadFromDhis2 = useCallback(() => {
    const committeeRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${COMMITTEE_PROGRAM}&pageSize=5000`,
    );
    const aapRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${AAP_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`,
    );
    const documentRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${DOCUMENTS_HUB_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`,
    );

    Promise.all([committeeRequest, aapRequest, documentRequest])
      .then(([committeeResult, aapResult, documentResult]) => {
        const committeeMembers = committeeResult.data?.instances || [];
        const aapInstances = aapResult.data?.instances || [];
        const documentInstances = documentResult.data?.instances || [];

        const committeeEventsPromise = committeeMembers.length > 0
          ? axios.get(`/tracker/events?program=${COMMITTEE_PROGRAM}&orgUnit=${districtId}&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });
        const aapEventsPromise = aapInstances.length > 0
          ? axios.get(`/tracker/events?program=${AAP_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });
        const documentEventsPromise = documentInstances.length > 0
          ? axios.get(`/tracker/events?program=${DOCUMENTS_HUB_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });

        return Promise.all([committeeEventsPromise, aapEventsPromise, documentEventsPromise])
          .then(([committeeEvents, aapEvents, documentEvents]) => {
            const committeeReports = committeeEvents.data?.instances || [];
            const aapReports = aapEvents.data?.instances || [];
            const docReports = documentEvents.data?.instances || [];

            let femaleCount = 0;
            const committeeRows = committeeMembers.map((item, index) => {
              const gender = getAttributeValue("Sex", item);
              if (gender === "Female") {
                femaleCount++;
              }

              const reportLink = getFirstFileLinkIfExist(
                committeeReports,
                APPOINTMENT_LETTER_FILE,
                item.trackedEntity,
                COMMITTEE_REPORT_STAGE,
              );

              return {
                key: item.trackedEntity || `committee-${index}`,
                no: index + 1,
                subCommittee: getAttributeValue("Sub Statutory Committee Department", item),
                gender,
                appointmentLetter: reportLink
                  ? buildFileUrl(reportLink, APPOINTMENT_LETTER_FILE)
                  : "Not Uploaded",
              };
            });

            const yearAap = isDpatScoreRelaxationExempt()
              ? filterRecordsByDpatYearAttribute(aapInstances, year)
              : (formatDataGeneral(aapInstances, "Year", `${year}`) || aapInstances);
            const genderActivities = yearAap.filter(isGenderFocusActivity);
            let supportedGenderActivities = 0;

            genderActivities.forEach((activity) => {
              const currentReport = aapReports.find((report) => report.trackedEntity === activity.trackedEntity);
              const isSupported = currentReport?.dataValues?.some(
                (value) => value.dataElement === ACTIVITY_STATUS_ELEMENT && value.value === "Completed",
              );
              if (isSupported) {
                supportedGenderActivities++;
              }
            });

            const femalePct = Number(calculatePercentage(femaleCount, committeeRows.length).toFixed(2));
            const genderActivityPct = Number(
              calculatePercentage(supportedGenderActivities, genderActivities.length).toFixed(2),
            );

            const tempAapDocs = formatDataGeneral(
              documentInstances,
              "DPAT | M&E Tool Type",
              "Annual Action Plan Progress Report",
            ) || [];
            const socialWelfareDocs = formatDataGeneral(
              documentInstances,
              "DPAT | M&E Tool Type",
              "Social Welfare Progress Report",
            ) || [];

            const aapLink = tempAapDocs.length > 0
              ? getFileLinkIfExist(docReports, MONITORING_REPORT_FILE, tempAapDocs[0].trackedEntity)
              : "";
            const swLink = socialWelfareDocs.length > 0
              ? getFileLinkIfExist(docReports, MONITORING_REPORT_FILE, socialWelfareDocs[0].trackedEntity)
              : "";

            setSubCommittees(committeeRows);
            const documentRows = [
              {
                key: "aap-progress-report",
                title: "Annual Action Plan Progress Report",
                link: aapLink ? buildFileUrl(aapLink, MONITORING_REPORT_FILE) : "Not Uploaded",
              },
              {
                key: "social-welfare-report",
                title: "Social Welfare Progress Report",
                link: swLink ? buildFileUrl(swLink, MONITORING_REPORT_FILE) : "Not Uploaded",
              },
            ];
            setDocuments(documentRows);
            setFemalePercentage(femalePct);
            setGenderActivityPercentage(genderActivityPct);
            setScorei(scoreOnlyIfAllEvidenceComplete({
              rows: committeeRows,
              rowLinkKeys: ["appointmentLetter"],
              documentLinks: documentRows,
              score: femalePct >= 20 ? 2 : 0,
            }));
            setScoreii(scoreOnlyIfDocumentLinksComplete(
              documentRows,
              genderActivityPct >= 80 ? 1 : 0,
            ));
          });
      })
      .catch((err) => console.log(err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.subCommittees) setSubCommittees(snap.subCommittees);
      else if (snap.members) setSubCommittees(snap.members);
      if (snap.data) setDocuments(snap.data);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
      if (snap.percentage != null) setFemalePercentage(snap.percentage);
      if (snap.genderActivityPercentage != null) setGenderActivityPercentage(snap.genderActivityPercentage);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const subCommitteeRows = useMemo(
    () => transformDpatTableRows(subCommittees, ["appointmentLetter"], {
      appointmentLetter: "View Appointment Letter",
    }),
    [subCommittees],
  );

  const subCommitteeColumns = useMemo(
    () => buildSearchableColumns(
      [
        { title: "No.", dataIndex: "no", key: "no" },
        { title: "Sub-Committee", dataIndex: "subCommittee", key: "subCommittee" },
        { title: "Gender", dataIndex: "gender", key: "gender" },
        { title: "Appointment Letter", dataIndex: "appointmentLetter", key: "appointmentLetter" },
      ],
      subCommitteeRows,
      {
        textSearchKeys: ["subCommittee"],
        selectFilterKeys: ["gender"],
      },
    ),
    [subCommitteeRows],
  );

  const documentColumns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Evidence", dataIndex: "link", key: "link" },
  ];

  return (
    <Comment
      data={documents}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi4.0-4.4-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            dhis2ProgramId={COMMITTEE_PROGRAM}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.4i Actual Score:" score={scorei} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.4ii Actual Score:" score={scoreii} />
          </IndicatorScoreSection>

          <Title level={4} style={{ marginTop: "20px" }}>I- List of Sub-Committee and their Gender</Title>
          <Table
            columns={subCommitteeColumns}
            dataSource={subCommitteeRows}
            pagination={getDpatTablePagination("members")}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>II- Evidence of Uploaded Documents</Title>
          <Table
            columns={documentColumns}
            dataSource={transformDpatTableRows(documents, ["link"], { link: "View Evidence" })}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={5} style={{ marginTop: "20px" }}>Conclusion</Title>
          <Typography.Paragraph>
            {femalePercentage}% of the statutory sub-committee members are Female.
          </Typography.Paragraph>
          <Typography.Paragraph>
            The District Assembly supports {genderActivityPercentage}% of Gender activities to promote equity
            and non-discrimination against women and girls in the District.
          </Typography.Paragraph>
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default DeepeningGenderMainstreaming;
